<template lang="pug">
.table-actions
  q-btn.actions-button(:disabled="diableActionsButton", :class="diableActionsButton && 'disabled'")
    inline-svg.grid-actions-icon(:src="require(`../../../../assets/icons/grid/actions.svg`)")
    q-menu#actions-menu
      q-list
        q-item(
          v-for="item in handlers",
          :key="item.name || item.label",
          :clickable="!isActionDisabled(item)",
          :class="{ 'action-item--disabled': isActionDisabled(item) }",
          :aria-disabled="isActionDisabled(item)",
          v-close-popup="!isActionDisabled(item)",
          @click="clickHandler(item)"
        )
          q-item-section
            q-item-label {{ item.label }}
          q-tooltip(
            v-if="getDisabledActionTooltip(item)",
            content-class="action-disable-tooltip",
            anchor="top middle",
            self="bottom middle"
          )
            span {{ getDisabledActionTooltip(item) }}
        q-separator(v-if="handlers.length > 0")
        q-item(
          clickable,
          v-close-popup,
          v-for="item in crud",
          :key="item.name || item.label",
          @click="clickCrud(item)"
        )
          q-item-section
            q-item-label {{ item.label }}

  q-dialog(v-model="showConfirm")
    confirm-modal(@confirm="confirmDelete", :parentData="{ message: notifies.confirm_destroy_entries }")
</template>

<script setup>
import { ref, computed, inject } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { actionsFn } from "@/services/useActions";
import { useDisabledActions } from "@/services/useDisabledActions";
import confirmModal from "../../helpers/confirmModal.vue";
import { notifies } from "@/services/useLocales";
import { Notify } from "quasar";

const openPrintAct = inject("openPrintAct", () => {});
const updateRow = inject("updateRow", () => {});

const props = defineProps({
  /** id текущего ряда. Пример: 1 */
  rowId: { type: Number, default: -1 },
  /** Все экшены текущего ряда. Пример:
  {
    "handlers": [
        {
            "action_type": "request",
            "name": "on_approve",
            "label": "Отправить на согласование",
            "verb": "patch",
            "path": "/acts/1/on_approve"
        }
    ],
    "crud": []
  }
   */
  actions: { type: Object, default: () => {} },
});

const emit = defineEmits([
  /** Эмит для открытия edit компонента страницы. В качестве параметра принимает id элемента. Пример: 1 */
  "open-edit",
  /** Эмит удаления элемента таблицы. В качестве параметра принимает id элемента. Пример: 1 */
  "destroy-table-item",
]);

const showConfirm = ref(false);

const rowId = computed(() => props.rowId);
const handlers = computed(() => props.actions?.handlers || []);
const crud = computed(() => props.actions?.crud || []);
const diableActionsButton = computed(() => handlers.value?.length === 0 && crud.value?.length === 0);
const { isActionDisabled, getDisabledActionTooltip } = useDisabledActions(handlers);

const clickHandler = async handler => {
  if (isActionDisabled(handler)) return;

  if (handler.action_type === "request") {
    try {
      const path = `/api/v3${handler.path}`;

      if (handler.verb === "patch") {
        const { data } = await backend.patch(path, {});
        if (data.message) Notify.create(data.message);
      } else if (handler.verb === "post") {
        const { data } = await backend.create(path, {});
        if (data.message) Notify.create(data.message);
      } else {
        const { data } = await backend.index(path, {});
        if (data.message) Notify.create(data.message);
      }
    } catch (err) {
      await handleError(err);
    } finally {
      updateRow(rowId.value);
    }
  } else if (handler.action_type === "js") {
    if (handler.onclick === "printAct") {
      const printData = await actionsFn?.[handler.onclick]?.(handler.data_url);
      openPrintAct(printData);
    } else {
      actionsFn?.[handler.onclick]?.();
    }
  }
};

const confirmDelete = confirm => {
  confirm && emit("destroy-table-item", rowId.value);
  showConfirm.value = false;
};

const clickCrud = async crud => {
  if (crud.name === "edit") {
    emit("open-edit", rowId.value);
  } else if (crud.name === "destroy") {
    showConfirm.value = true;
  }
};
</script>

<style lang="scss" scoped>
.table-actions {
  width: fit-content;
  margin: auto;
  position: relative;

  .actions-button {
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background-color: var(--index-grid-actions-icon);

    &:hover {
      background-color: #5b7aff;
    }

    &.disabled {
      &:hover {
        background-color: var(--index-grid-actions-icon);
      }
    }
  }

  .actions-list {
    width: fit-content;
    background: var(--grid-actions-menu-background);
    box-sizing: border-box;
    box-shadow: var(--grid-actions-box-shadow);
    color: var(--grid-actions-menu-color);
    border-radius: 8px;
    overflow-y: auto;
    max-height: 250px !important;
    position: absolute;
    left: 0;
    top: 36px;
    z-index: 1;

    &-item {
      line-height: 48px;
      height: 48px;
      padding: 0 16px;
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        background: var(--grid-actions-menu-hover);
      }
    }
  }

  .action-item--disabled {
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
.action-item--disabled {
  background: rgba(138, 144, 153, 0.08) !important;
  color: #8a9099 !important;
  cursor: not-allowed;

  &:hover {
    background: rgba(138, 144, 153, 0.12) !important;
  }

  .q-item__label {
    color: #8a9099 !important;
  }
}

.action-disable-tooltip {
  background: var(--index-grid-active-row-background) !important;
  color: #ffffff !important;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  padding: 12px 16px;
}

.table-actions {
  .q-btn__wrapper:before {
    box-shadow: none;
  }

  .actions-button {
    .q-btn__wrapper,
    .q-btn__content {
      cursor: pointer;
    }
    .grid-actions-icon path {
      fill: var(--index-grid-actions-icon-fill);
    }

    &:hover {
      .grid-actions-icon path {
        fill: #fff;
      }
    }

    &.disabled {
      .q-focus-helper {
        opacity: 0 !important;
      }
      &:hover {
        .grid-actions-icon path {
          fill: var(--index-grid-actions-icon-fill);
        }
      }
    }
  }
}
</style>
